import { Container, Stack } from '@mui/material';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { useUpdateEffect } from 'react-use';

import { PejHttpError } from '@common-lib';
import { RiveAnimation, RiveAnimationRef } from '@frontend-libs/portal';

import { authClient } from '@portal-clients/auth';
import { configurationClient } from '@portal-clients/configuration';
import { UserModel, userClient } from '@portal-clients/user';

import { AppRoutes } from '@frontend/app-routes';
import {
  LocaleProvider,
  UpdateProvider,
  useClientQuery,
} from '@frontend/hooks';
import { useI18n } from '@frontend/locales';
import { ErrorView, RouterErrorView } from '@frontend/views';

import { AccountTokenProvider } from './account-token-context';
import { PortalConfigurationProvider } from './configuration-context';
import { LoadingProvider } from './loading-context';
import { Prefetch } from './prefetch';
import { useRoutesImporter } from './use-routes-importer';
import { useSplashScreenHider } from './use-splash-screen-hider';
import { UserProvider } from './user-context';

export interface InitializeProps {
  sessionValidationKey: number;
  isValidSession: boolean;
  onValidSessionChanged(isValid: boolean): void;
}

export const Initialize = ({
  sessionValidationKey,
  isValidSession,
  onValidSessionChanged,
}: PropsWithChildren<InitializeProps>) => {
  const {
    loading: i18nLoading,
    error,
    language,
    locale,
    setLanguage,
  } = useI18n();
  const { routes, loading: routesLoading } = useRoutesImporter();
  const riveRef = useRef<RiveAnimationRef>(null);
  const handleTrigger = (inputName: string, value?: number | boolean) =>
    riveRef.current?.trigger(inputName, value);
  const [isLoadingAnimationComplete, setIsLoadingAnimationComplete] =
    useState(false); // rive emits event "complete" when loading-animation is finished.
  const [user, setUser] = useState<UserModel | undefined>(undefined);

  const { hideSplashScreen } = useSplashScreenHider();
  const queryClient = useQueryClient();

  const activeRouter = useMemo(
    () =>
      createBrowserRouter([
        {
          path: '*',
          element: <AppRoutes validSession={isValidSession} routes={routes!} />,
          errorElement: <RouterErrorView />,
        },
      ]),
    [isValidSession, routes],
  );

  useUpdateEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['verify-session'] });
    setIsLoadingAnimationComplete(false);
  }, [sessionValidationKey]);

  const { isFetching: loadingVerification } = useClientQuery({
    queryKey: ['verify-session'],
    onFetch: async () => {
      return await authClient
        .verify()
        .then((verified: boolean) => {
          onValidSessionChanged(verified);
          return verified;
        })
        .catch(() => {
          onValidSessionChanged(false);
          return false;
        });
    },
  });

  useClientQuery({
    queryKey: ['user'],
    onFetch: async () => {
      let activeUser: UserModel;

      try {
        activeUser = await userClient.getActive();
      } catch (error) {
        if ((error as PejHttpError).statusCode === 404) {
          onValidSessionChanged(false);
        }
        throw error;
      }

      setLanguage(activeUser.locale);
      setUser(activeUser);

      return activeUser;
    },
    enabled: isValidSession && !i18nLoading,
  });

  const { data: config, isError: configError } = useClientQuery({
    queryKey: ['portal-configuration'],
    onFetch: async () => {
      return await configurationClient.get();
    },
    onError: (error) => Sentry.captureException(error),
    enabled: isValidSession,
    // Config is required to render the app — retry aggressively on transient
    // failures but don't retry client errors (4xx) which won't self-resolve.
    retry: (failureCount: number, error: Error) => {
      if (error instanceof PejHttpError && error.statusCode < 500) {
        return false;
      }

      return failureCount < 5;
    },
    retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 30000),
  });

  // Keep loading until config resolves (success or error). Without this,
  // a query error (isPending=false, data=undefined) clears the loading guard
  // and the Provider renders with config=undefined, crashing any component
  // that reads config.features.
  const isSessionDataLoading = isValidSession
    ? !user || (!config && !configError)
    : false;

  const isLoading =
    i18nLoading || routesLoading || loadingVerification || isSessionDataLoading;

  useEffect(() => {
    if (!isLoading) {
      handleTrigger('success', true);
    }
  }, [isLoading]);

  if (error) {
    return <>{`Failed to load locales: ${error}`}</>;
  }

  return (
    <>
      {!isLoadingAnimationComplete && (
        <Container component="main" maxWidth="xs">
          <Stack
            spacing={2}
            height="100vh"
            justifyContent="center"
            alignItems="center"
          >
            <RiveAnimation
              ref={riveRef}
              src="/rive/loading.riv"
              height={128}
              onEvent={() => {
                hideSplashScreen();
                setIsLoadingAnimationComplete(true);
              }}
            />
          </Stack>
        </Container>
      )}
      {isLoadingAnimationComplete && isValidSession && user && config && (
        <UpdateProvider>
          <PortalConfigurationProvider config={config} language={language}>
            <UserProvider user={user} setUser={setUser}>
              <AccountTokenProvider>
                <Prefetch accounts={user.accounts}>
                  <LocaleProvider language={language} locale={locale}>
                    <LoadingProvider>
                      <RouterProvider router={activeRouter} />
                    </LoadingProvider>
                  </LocaleProvider>
                </Prefetch>
              </AccountTokenProvider>
            </UserProvider>
          </PortalConfigurationProvider>
        </UpdateProvider>
      )}

      {isLoadingAnimationComplete && isValidSession && user && configError && (
        <ErrorView />
      )}

      {isLoadingAnimationComplete && !isValidSession && (
        <PortalConfigurationProvider config={undefined} language={language}>
          <UserProvider user={undefined} setUser={setUser}>
            <Prefetch>
              <LocaleProvider language={language} locale={locale}>
                <LoadingProvider>
                  <RouterProvider router={activeRouter} />
                </LoadingProvider>
              </LocaleProvider>
            </Prefetch>
          </UserProvider>
        </PortalConfigurationProvider>
      )}
    </>
  );
};
